import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ServerErrorCode } from '@planning/types';
import { AuthService, AuthTokensService, TOKEN_TYPE } from '@unified-ui/shared/auth-lib';
import { PlatformSignalService } from '@unified-ui/shared/services';
import { AuthSession } from 'aws-amplify/auth';
import { Observable, TimeoutError, catchError, mergeMap, of, switchMap, throwError, timeout } from 'rxjs';

// Defines the expected structure of an error with HttpResponse.
export interface ErrorResponse {
    extensions?: { code?: ServerErrorCode };
}

@Injectable()
export class ErrorHandlerInterceptor<T> implements HttpInterceptor {
    private readonly _signalService = inject(PlatformSignalService);
    private readonly _authService = inject(AuthService);
    private readonly _authTokensService = inject(AuthTokensService);

    intercept(req: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
        const requestWithLatestToken = () => {
            const newAuthToken = this._authTokensService.getAuthToken(TOKEN_TYPE.ACCESS);
            return req.clone({
                setHeaders: {
                    Authorization: `Bearer ${newAuthToken}`,
                    OrgId: this._authTokensService.getOrgId()
                }
            });
        };
        return next.handle(requestWithLatestToken()).pipe(
            timeout(300_000),
            mergeMap(response => {
                if (response instanceof HttpResponse && response.body && response.body.errors && response.body.errors.length > 0) {
                    const errors: ErrorResponse[] = response.body?.errors;
                    // invalid token or session expired, try to refresh the token
                    const hasUnauthenticatedError = errors.some(error => error?.extensions?.code === ServerErrorCode.Unauthenticated);
                    if (hasUnauthenticatedError) {
                        return this._authService.getAuthSession().pipe(
                            switchMap((session: AuthSession) => {
                                this._authTokensService.updateAuthTokens(session);
                                // Retry the failed request with the latest token
                                return next.handle(requestWithLatestToken());
                            }),
                            catchError(refreshError => {
                                // If refresh fails, log out the user
                                // logout the user after 2 seconds
                                this._dispatchError('Session Expired', 'Please login again');
                                let countdown = 2;
                                const interval = setInterval(() => {
                                    countdown--;
                                    if (countdown <= 0) {
                                        clearInterval(interval);
                                        this._authService.logOut();
                                    }
                                }, 1000);
                                return throwError(() => refreshError);
                            })
                        );
                    } else return of(response);
                } else return of(response);
            }),
            catchError(err => {
                if (err instanceof TimeoutError) {
                    this._dispatchError(err.name, err.message);
                } else if (err.status === 0 && !navigator.onLine) {
                    this._dispatchError('Connection Error', 'No internet connection');
                } else if (err.status === 401) {
                    // If token has expired, refresh it
                    return this._authService.getAuthSession().pipe(
                        switchMap((session: AuthSession) => {
                            this._authTokensService.updateAuthTokens(session);
                            // Retry the failed request with the latest token
                            return next.handle(requestWithLatestToken());
                        }),
                        catchError(refreshError => {
                            // If refresh fails, log out the user
                            this._authService.logOut();
                            return throwError(() => refreshError);
                        })
                    );
                } else if (err.status === 403) {
                    this._dispatchError('Unauthorized', 'Contact your administrator');
                } else {
                    // Comment the following line to avoid showing all other errors to the user
                    this._dispatchError(err.name, 'Request Error');
                }
                console.error({ err, request: req });
                return throwError(() => err);
            })
        );
    }

    private _dispatchError(summary: string, detail: string) {
        this._signalService.platformSignal = {
            severity: 'error',
            summary,
            detail
        };
    }
}
